import { appearanceOf } from "./appearance";
import { StoreHeader, StoreHero, MediaGallery, ProductReviews } from "./StoreExperience";
import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  ShoppingBag,
  ArrowUpRight,
  ArrowRight,
  Truck,
  ShieldCheck,
  Heart,
  Plus,
  Minus,
  Trash2,
  Check,
  Package,
  UserRound,
  Play,
} from "lucide-react";
import {
  Brand,
  Button,
  Field,
  Modal,
  SearchBox,
  ProductCard,
  ProductImage,
  Quantity,
  Video,
  Empty,
} from "./ui";
import { money, uid, priceFor, round, safeUrl } from "./domain";
import { api, config, demo } from "./data";
import { receiptPNG } from "./receipts";
function Turnstile({ onToken, nonce }) {
  const ref = useRef();
  useEffect(() => {
    if (demo || !config.turnstileSiteKey) return;
    let id,
      stop = false;
    const mount = () => {
      if (!stop && window.turnstile && ref.current)
        id = window.turnstile.render(ref.current, {
          sitekey: config.turnstileSiteKey,
          action: "checkout",
          callback: onToken,
          "expired-callback": () => onToken(""),
          "error-callback": () => onToken(""),
        });
    };
    if (window.turnstile) mount();
    else {
      let script = document.querySelector("[data-turnstile]");
      if (!script) {
        script = document.createElement("script");
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.dataset.turnstile = "true";
        document.head.append(script);
      }
      script.addEventListener("load", mount, { once: true });
    }
    return () => {
      stop = true;
      if (id != null) window.turnstile?.remove(id);
    };
  }, [nonce]);
  return <div ref={ref} />;
}
export function ProductDetail({ product, data, onClose, onAdd }) {
  const variants = data.variants.filter(
    (v) => v.product_id === product.id && v.active,
  );
  const [id, setId] = useState(
    variants.find((v) => v.stock > 0)?.id || variants[0]?.id,
  );
  const [quantity, setQuantity] = useState(1);
  const variant = variants.find((v) => v.id === id);
  return (
    <Modal title={product.name} onClose={onClose} wide>
      <div className="product-detail">
        <div><MediaGallery product={product} variant={variant}/></div>
        <div className="detail-copy">
          <span className="eyebrow">HECHO PARA ACOMPAÑARTE</span>
          <h2>{product.name}</h2>
          <p className="muted">Código: {variant?.sku || product.sku}</p>
          <div className="product-price large">
            <strong>{money(priceFor(product, quantity))}</strong>
            {product.compare_price > product.price && (
              <del>{money(product.compare_price)}</del>
            )}
          </div>
          <p className="pre-line">{product.description}</p>
          <h4>Color: {variant?.color || "Sin variantes"}</h4>
          <div className="swatches">
            {variants.map((v) => (
              <button
                key={v.id}
                className={id === v.id ? "selected" : ""}
                aria-label={`Color ${v.color}`}
                title={`${v.color} · ${v.stock} disponibles`}
                onClick={() => {
                  setId(v.id);
                  setQuantity(1);
                }}
              >
                <span style={{ background: v.hex }} />
                {v.color}
              </button>
            ))}
          </div>
          <p className={variant?.stock > 0 ? "stock-available" : "error-text"}>
            {variant?.stock || 0} unidades disponibles
          </p>
          {product.wholesale_price > 0 && (
            <p className="notice">
              Desde {product.wholesale_min} unidades:{" "}
              {money(product.wholesale_price)} cada una.
            </p>
          )}
          <div className="flex">
            <Quantity
              value={quantity}
              onChange={setQuantity}
              max={variant?.stock || 1}
            />
            <Button
              disabled={!variant?.stock}
              onClick={() => onAdd(product, variant, quantity)}
            >
              <ShoppingBag size={18} /> Agregar al pedido
            </Button>
          </div>
          <div className="product-perks">
            <span>
              <ShieldCheck size={18} /> Pago por Yape, Plin o transferencia
            </span>
            <span>
              <Truck size={18} /> Delivery o recojo en tienda
            </span>
          </div>
        </div>
      </div>
      <ProductReviews productId={product.id}/>
    </Modal>
  );
}
export function Checkout({
  data,
  items,
  onClose,
  onComplete,
  manual = false,
  onRemove,
  onQuantity,
  run,
}) {
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    address: "",
    delivery: "delivery",
    payment: "Yape",
    notes: "",
    collaborator_id: "",
    preorder: false,
  });
  const [token, setToken] = useState(""),
    [nonce, setNonce] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const key = useRef(uid());
  const lines = items
    .map((i) => {
      const v = data.variants.find((v) => v.id === i.variant_id),
        p = data.products.find((p) => p.id === v?.product_id);
      return { item: i, v, p };
    })
    .filter((x) => x.p && x.v);
  const subtotal = round(
    lines.reduce(
      (s, { item, p }) =>
        s +
        priceFor(
          p,
          lines
            .filter((x) => x.p.id === p.id)
            .reduce((s, x) => s + x.item.quantity, 0),
        ) *
          item.quantity,
      0,
    ),
  );
  const shipping =
    form.delivery === "pickup" ||
    (data.store.free_shipping > 0 && subtotal >= data.store.free_shipping)
      ? 0
      : Number(data.store.shipping_fee);
  const set = (k, v) => setForm({ ...form, [k]: v });
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const order = await api.createOrder(
        { ...form, items, request_key: key.current, turnstile_token: token },
        manual,
      );
      onComplete(order);
    } catch (err) {
      setError(err.message);
      setToken("");
      setNonce((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={manual ? "Confirmar pedido manual" : "Tu pedido"}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit} className="checkout-grid">
        <div>
          <h3>Datos del cliente</h3>
          <div className="form-grid">
            <Field
              label="Nombre completo"
              required
              value={form.customer_name}
              onChange={(e) => set("customer_name", e.target.value)}
              maxLength={120}
            />
            <Field
              label="Celular"
              type="tel"
              required
              pattern="[+0-9 ()\-]{7,20}"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+51 999 999 999"
            />
            <Field
              label="Correo electrónico"
              type="email"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              maxLength={160}
            />
            <Field label="Entrega">
              <select
                value={form.delivery}
                onChange={(e) => set("delivery", e.target.value)}
              >
                <option value="delivery">Delivery</option>
                <option value="pickup">Recojo en tienda</option>
              </select>
            </Field>
          </div>
          {form.delivery === "delivery" ? (
            <Field
              label="Dirección y referencia"
              required
              maxLength={400}
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
            />
          ) : (
            <p className="notice">
              Recojo:{" "}
              {data.store.address || "Coordina la dirección con la tienda."}
            </p>
          )}
          <h3>Forma de pago</h3>
          <div className="payment-options">
            {["Yape", "Plin", "Transferencia"].map((method) => (
              <label
                className={form.payment === method ? "chosen" : ""}
                key={method}
              >
                <input
                  type="radio"
                  name="payment"
                  checked={form.payment === method}
                  onChange={() => set("payment", method)}
                />
                {method}
              </label>
            ))}
          </div>
          <div className="notice pre-line">
            {form.payment === "Yape"
              ? data.store.yape
              : form.payment === "Plin"
                ? data.store.plin
                : data.store.bank ||
                  "Coordina los datos de pago con la tienda."}
            <p>{data.store.payment_instructions}</p>
          </div>
          <Field label="Notas del pedido">
            <textarea
              value={form.notes}
              maxLength={500}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Field>
          {manual && (
            <>
              <Field label="Colaborador">
                <select
                  value={form.collaborator_id}
                  onChange={(e) => set("collaborator_id", e.target.value)}
                >
                  <option value="">General</option>
                  {data.members.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name || m.email}
                    </option>
                  ))}
                </select>
              </Field>
              <label className="check-line">
                <input
                  type="checkbox"
                  checked={form.preorder}
                  onChange={(e) => set("preorder", e.target.checked)}
                />{" "}
                Registrar como prepedido (reserva stock)
              </label>
            </>
          )}
          {!manual && (
            <>
              <label className="check-line">
                <input type="checkbox" required /> Autorizo el uso de mis datos
                para atender y entregar este pedido.
              </label>
              <Turnstile onToken={setToken} nonce={nonce} />
            </>
          )}
        </div>
        <aside className="order-summary">
          <h3>
            Resumen de productos{" "}
            <span>{items.reduce((s, i) => s + i.quantity, 0)}</span>
          </h3>
          {lines.map(({ item, v, p }) => (
            <div className="cart-line" key={v.id}>
              <ProductImage product={p} variant={v} />
              <div>
                <strong>{p.name}</strong>
                <small>
                  {v.color} · {v.sku}
                </small>
                <Quantity
                  value={item.quantity}
                  max={v.stock}
                  onChange={(q) => onQuantity(v.id, q)}
                />
              </div>
              <button
                type="button"
                className="icon-btn danger"
                aria-label={`Quitar ${p.name}`}
                onClick={() => onRemove(v.id)}
              >
                <Trash2 size={17} />
              </button>
            </div>
          ))}
          <div className="totals">
            <p>
              Subtotal <span>{money(subtotal)}</span>
            </p>
            <p>
              Envío <span>{money(shipping)}</span>
            </p>
            <h3>
              Total <span>{money(subtotal + shipping)}</span>
            </h3>
          </div>
          {error && (
            <p role="alert" className="error-text">
              {error}
            </p>
          )}
          <Button
            type="submit"
            className="full"
            disabled={busy || !items.length || (!manual && !demo && !token)}
          >
            {busy
              ? "Registrando…"
              : manual
                ? "Crear pedido"
                : "Confirmar pedido"}
            <ArrowRight size={18} />
          </Button>
          <small>
            El pago se confirma después de verificar el abono.
            {demo ? " Esta compra es de demostración." : ""}
          </small>
        </aside>
      </form>
    </Modal>
  );
}
export function Storefront({ data, run, refresh, notify }) {
  const [query, setQuery] = useState(""),
    [category, setCategory] = useState("all"),
    [sort, setSort] = useState("recommended"),
    [product, setProduct] = useState(null),
    [checkout, setCheckout] = useState(false),
    [success, setSuccess] = useState(null),
    [page, setPage] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("dilo-cart") || "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    sessionStorage.setItem("dilo-cart", JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    if (!sessionStorage.getItem("dilo-visited")) {
      api.visit().catch(() => {});
      sessionStorage.setItem("dilo-visited", "yes");
    }
  }, []);
  useEffect(() => {
    const context = document.modelContext;
    if (!context?.registerTool) return;
    const control = new AbortController();
    Promise.resolve(
      context.registerTool(
        {
          name: "search_dilo_products",
          title: "Buscar productos de Dilo Shop",
          description:
            "Filtra el catálogo visible por nombre o código. No realiza compras.",
          inputSchema: {
            type: "object",
            properties: { query: { type: "string", maxLength: 120 } },
            required: ["query"],
            additionalProperties: false,
          },
          annotations: { readOnlyHint: true, untrustedContentHint: true },
          execute(input) {
            if (typeof input.query !== "string" || input.query.length > 120)
              throw new Error("Consulta inválida");
            setQuery(input.query);
            return {
              products: data.products
                .filter(
                  (p) =>
                    p.active &&
                    `${p.name} ${p.sku}`
                      .toLowerCase()
                      .includes(input.query.toLowerCase()),
                )
                .map((p) => ({ name: p.name, price: p.price, sku: p.sku })),
            };
          },
        },
        { signal: control.signal },
      ),
    ).catch(() => {});
    return () => control.abort();
  }, [data.products]);
  const store = data.store;
  const appearance = appearanceOf(store);
  const categories = data.categories
    .filter((c) => c.active && !c.parent_id)
    .sort(
      (a, b) =>
        Number(b.featured) - Number(a.featured) || a.sort_order - b.sort_order,
    );
  const isCategoryActive = (id) => {
    const c = data.categories.find((c) => c.id === id);
    return (
      !id ||
      (!!c?.active &&
        (!c.parent_id ||
          !!data.categories.find((p) => p.id === c.parent_id)?.active))
    );
  };
  const products = data.products
    .filter(
      (p) =>
        p.active &&
        (sort !== "favorites" || p.featured) &&
        (sort !== "offers" || p.compare_price > p.price) &&
        isCategoryActive(p.category_id) &&
        (category === "all" ||
          p.category_id === category ||
          data.categories.find((c) => c.id === p.category_id)?.parent_id ===
            category) &&
        `${p.name} ${p.sku}`.toLowerCase().includes(query.toLowerCase()),
    )
    .sort((a, b) =>
      sort === "low"
        ? a.price - b.price
        : sort === "high"
          ? b.price - a.price
          : sort === "name"
            ? a.name.localeCompare(b.name)
            : a.sort_order - b.sort_order,
    );
  function add(p, v, q) {
    const current = cart.find((i) => i.variant_id === v.id)?.quantity || 0;
    if (current + q > v.stock) {
      notify("La cantidad supera el stock disponible.", "error");
      return;
    }
    setCart([
      ...cart.filter((i) => i.variant_id !== v.id),
      { variant_id: v.id, quantity: current + q },
    ]);
    setProduct(null);
    notify(`${p.name} agregado al pedido`);
  }
  const updateQuantity = (id, q) =>
    setCart(cart.map((i) => (i.variant_id === id ? { ...i, quantity: q } : i)));
  const whatsapp = store.whatsapp.replace(/\D/g, "");
  return (
    <div className="storefront">
      {demo && (
        <div className="demo-strip">
          MODO DEMOSTRACIÓN · pedidos y cambios guardados solo en este navegador{" "}
          <a href="/config">
            Administrar tienda <ArrowUpRight size={13} />
          </a>
        </div>
      )}
      <div className="announcement">
        {store.announcement}
        <span>Yape · Plin · Transferencia</span>
      </div>
      <StoreHeader appearance={appearance}>
        <Brand logo={store.logo} />
        <nav>
          {(appearance.menu || ["all", ...categories.map(c=>c.id), "offers", "favorites"]).map(id => {
            const label = {all:"La tienda",offers:"Ofertas",favorites:"Destacados"}[id] || data.categories.find(c=>c.id===id && c.active)?.name;
            return label ? <button key={id} onClick={()=>{setQuery("");setCategory(["all","offers","favorites"].includes(id)?"all":id);setSort(["offers","favorites"].includes(id)?id:"recommended");document.getElementById("catalogo")?.scrollIntoView({behavior:"smooth"});}}>{label}</button> : null;
          })}
          {store.blog?.length > 0 && (
            <button
              onClick={() =>
                setPage({
                  title: "El diario de Dilo",
                  body: store.blog
                    .map((p) => p.title + "\n" + p.body)
                    .join("\n\n"),
                })
              }
            >
              El diario
            </button>
          )}
        </nav>
        <div className="header-actions">
          <a
            className="icon-btn"
            href="/config"
            aria-label="Acceso administradores"
          >
            <UserRound size={21} />
          </a>
          <button className="cart-button" onClick={() => setCheckout(true)}>
            <ShoppingBag size={18} />
            <span>Mi bolsa</span>
            <b>{cart.reduce((s, i) => s + i.quantity, 0)}</b>
          </button>
        </div>
      </StoreHeader>
      {store.published === false && !demo ? (
        <Empty
          title="Volvemos muy pronto"
          description="Estamos preparando nuestros próximos favoritos."
        />
      ) : (
        <>
          <StoreHero store={store}/>
          <section className="shop-benefits">
            <span>
              <Truck size={19} /> Delivery y recojo en tienda
            </span>
            <span>
              <ShieldCheck size={19} /> Atención personal en cada compra
            </span>
            <span>
              <Heart size={19} /> Detalles que hacen la diferencia
            </span>
          </section>
          <main className="catalog" id="catalogo">
            <div className="catalog-heading">
              <div>
                <span className="eyebrow">ELIGE. COMBINA. DILO.</span>
                <h2>
                  Encuentra lo que va contigo<span>✦</span>
                </h2>
              </div>
              <SearchBox
                value={query}
                onChange={setQuery}
                placeholder="Busca tu próximo favorito"
              />
            </div>
            <div className="catalog-tools">
              <div className="category-pills">
                <button
                  className={category === "all" ? "active" : ""}
                  onClick={() => setCategory("all")}
                >
                  Todos{" "}
                  <small>
                    {
                      data.products.filter(
                        (p) => p.active && isCategoryActive(p.category_id),
                      ).length
                    }
                  </small>
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    className={category === c.id ? "active" : ""}
                    onClick={() => setCategory(c.id)}
                  >
                    {c.icon} {c.name}
                  </button>
                ))}
              </div>
              <select
                aria-label="Orden de productos"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="recommended">Recomendados</option>
                <option value="favorites">Nuestros favoritos</option>
                <option value="offers">Ofertas</option>
                <option value="low">Precio: menor a mayor</option>
                <option value="high">Precio: mayor a menor</option>
                <option value="name">Nombre A–Z</option>
              </select>
            </div>
            {category !== "all" && (
              <div className="category-pills sub-pills">
                {data.categories
                  .filter((c) => c.parent_id === category && c.active)
                  .map((c) => (
                    <button key={c.id} onClick={() => setCategory(c.id)}>
                      {c.name}
                    </button>
                  ))}
              </div>
            )}
            {products.length ? (
              <div className="products-grid">
                {products.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    variants={data.variants}
                    onSelect={setProduct}
                  />
                ))}
              </div>
            ) : (
              <Empty
                title="No encontramos productos"
                description="Prueba con otra búsqueda o categoría."
              />
            )}
          </main>
        </>
      )}
      <footer className="shop-footer editable-footer" style={{backgroundColor:appearance.footer.color,color:appearance.footer.textColor,backgroundImage:safeUrl(appearance.footer.background)?`url("${safeUrl(appearance.footer.background)}")`:undefined}}>
        <div>
          <Brand logo={store.logo} />
          <p>{appearance.footer.title}</p>
        </div>
        <div>
          <h4>{appearance.footer.contactTitle}</h4>
          {whatsapp && (
            <a
              href={`https://wa.me/${whatsapp}`}
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp <ArrowUpRight size={15} />
            </a>
          )}
          {store.email && <a href={`mailto:${store.email}`}>{store.email}</a>}
          <p>{store.address}</p>
        </div>
        <div>
          <h4>{appearance.footer.linksTitle}</h4>
          {["instagram", "facebook", "tiktok"].map(
            (k) =>
              safeUrl(store[k]) && (
                <a
                  key={k}
                  href={safeUrl(store[k])}
                  target="_blank"
                  rel="noreferrer"
                >
                  {k.charAt(0).toUpperCase() + k.slice(1)}{" "}
                  <ArrowUpRight size={14} />
                </a>
              ),
          )}
          {store.pages?.map((p, i) => (
            <button key={i} onClick={() => setPage(p)}>
              {p.title}
            </button>
          ))}
          <a href="/config">Administrar mi tienda</a>
        </div>
        <div className="footer-bottom">
          © {new Date().getFullYear()} {store.name}
          <span>{appearance.footer.bottomText}</span>
        </div>
      </footer>
      {product && (
        <ProductDetail
          product={product}
          data={data}
          onClose={() => setProduct(null)}
          onAdd={add}
        />
      )}
      {checkout &&
        (cart.length ? (
          <Checkout
            data={data}
            items={cart}
            onClose={() => setCheckout(false)}
            onRemove={(id) => setCart(cart.filter((i) => i.variant_id !== id))}
            onQuantity={updateQuantity}
            onComplete={(o) => {
              setSuccess(o);
              setCart([]);
              setCheckout(false);
              refresh();
            }}
          />
        ) : (
          <Modal title="Tu bolsa" onClose={() => setCheckout(false)}>
            <Empty
              title="Tu próximo favorito te espera"
              description="Agrega productos para crear tu pedido."
              action={
                <Button onClick={() => setCheckout(false)}>
                  Explorar productos
                </Button>
              }
            />
          </Modal>
        ))}
      {success && (
        <Modal
          title="¡Tu pedido está registrado!"
          onClose={() => setSuccess(null)}
        >
          <div className="success-icon">
            <Check size={35} />
          </div>
          <h2>{success.number}</h2>
          <p>
            Recibimos tu pedido por {money(success.total)}. Confirma el pago con
            la tienda para continuar.
          </p>
          <p className="notice pre-line">
            {success.payment === "Yape"
              ? store.yape
              : success.payment === "Plin"
                ? store.plin
                : store.bank}
            <br />
            {store.payment_instructions}
          </p>
          <div className="stack">
            <Button onClick={() => receiptPNG(success, store)}>
              Descargar resumen PNG
            </Button>
            <a
              className="btn outline"
              href={`#order/${success.tracking_token || success.id}`}
            >
              Ver mi pedido y adjuntar pago
            </a>
            {whatsapp && (
              <a
                className="btn outline"
                href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hola, realicé el pedido ${success.number} por ${money(success.total)}. Quiero coordinar mi pago por ${success.payment}.`)}`}
                target="_blank"
                rel="noreferrer"
              >
                Coordinar por WhatsApp <ArrowUpRight size={17} />
              </a>
            )}
          </div>
        </Modal>
      )}
      {page && (
        <Modal title={page.title} onClose={() => setPage(null)}>
          <div className="pre-line">{page.body}</div>
        </Modal>
      )}
    </div>
  );
}
